import React from 'react'
import './PrivacyPolicy.css'

const PrivacyPolicy = () => {
  return (
    <div className='privacy-page'>

      <div className='privacy-hero'>
        <h1>Privacy Policy</h1>
        <p className='privacy-subtitle'>ArrAndArr — Privacy Policy</p>
        <p className='privacy-version'>July 2026 | Version 1.0</p>
      </div>

      <div className='privacy-content'>

        <section>
          <h2>Introduction</h2>
          <p>Welcome to our privacy policy. We are ArrAndArr and this is our plan of action when it comes to protecting your privacy. We respect your privacy and take the protection of personal information very seriously. The purpose of this policy is to describe the way that we collect, store, use and protect information that can be associated with you or another specific natural or juristic person and can be used to identify you or that person (<strong>personal information</strong>).</p>
        </section>

        <section>
          <h2>Audience</h2>
          <p>This policy applies to you if you are:</p>
          <ul>
            <li>a visitor to our website; or</li>
            <li>a customer who has ordered or requested the goods or services that we provide.</li>
          </ul>
        </section>

        <section>
          <h2>Personal Information</h2>
          <p>Personal information includes:</p>
          <ul>
            <li>certain information that we collect automatically when you visit our website;</li>
            <li>certain information collected on registration;</li>
            <li>certain information collected on submission; and</li>
            <li>optional information that you provide to us voluntarily;</li>
          </ul>
          <p>but excludes:</p>
          <ul>
            <li>information that has been made anonymous so that it does not identify a specific person;</li>
            <li>permanently de-identified information that does not relate or cannot be traced back to you specifically;</li>
            <li>non-personal statistical information collected and compiled by us; and</li>
            <li>information that you have provided voluntarily in an open public environment or forum including any blog, chat room, community, classifieds or discussion board.</li>
          </ul>
        </section>

        <section>
          <h2>Common Examples</h2>
          <p>Common examples of the types of personal information which we may collect and process include your:</p>
          <ul>
            <li><strong>identifying information</strong> — such as your name, date of birth or identification number of any kind;</li>
            <li><strong>contact information</strong> — such as your phone number or email address;</li>
            <li><strong>address information</strong> — such as your physical or postal address; or</li>
            <li><strong>demographic information</strong> — such as your gender or marital status.</li>
          </ul>
        </section>

        <section>
          <h2>Payment Information</h2>
          <p>Payment information is <strong>not</strong> stored by ArrAndArr and is merely passed via encryption to our third-party payment provider in order to process payment.</p>
        </section>

        <section>
          <h2>Acceptance</h2>
          <p>You must accept all the terms of this policy when you order our goods or request our services. If you do not agree with anything in this policy, then you may not order our goods or request our services.</p>
          <p>You may not access our website or order our goods or request our services if you are younger than 18 years old or do not have legal capacity to conclude legally binding contracts.</p>
          <p>By accepting this policy, you are deemed to have read, understood, accepted, and agreed to be bound by all of its terms.</p>
        </section>

        <section>
          <h2>Collection</h2>
          <h3>On Registration</h3>
          <p>Once you register on our website, you will no longer be anonymous to us. You will provide us with certain personal information when you register on our website. This personal information will include:</p>
          <ul>
            <li>your name and surname;</li>
            <li>your email address;</li>
            <li>your telephone number;</li>
            <li>your postal address or street address; and</li>
            <li>your username and password.</li>
          </ul>

          <h3>On Order or Request</h3>
          <p>When you order our goods or request our services from us, you will be asked to provide us with additional information on a voluntary basis.</p>

          <h3>From Browser</h3>
          <p>We automatically receive and record Internet usage information on our server logs from your browser, such as your Internet Protocol address (<strong>IP address</strong>), browsing habits, click patterns, version of software installed, system type, screen resolutions, colour capabilities, plug-ins, language settings, cookie preferences, search engine keywords, JavaScript enablement, the content and pages that you access on the website and the dates and times that you visit the website, paths taken and time spent on sites and pages within the website (<strong>usage information</strong>).</p>

          <h3>Cookies</h3>
          <p>We may place small text files called 'cookies' on your device when you visit our website. These files do not contain personal information, but they do contain a personal identifier allowing us to associate your personal information with a certain device. These files serve a number of useful purposes for you including:</p>
          <ul>
            <li>tailoring our website's functionality to you personally by letting us remember your preferences;</li>
            <li>improving how our website performs;</li>
            <li>allowing third parties to provide services to our website; and</li>
            <li>helping us deliver targeted advertising where appropriate in compliance with the applicable laws.</li>
          </ul>
          <p>Your internet browser generally accepts cookies automatically, but you can often change this setting to stop accepting them. You can also delete cookies manually. However, no longer accepting cookies or deleting them will prevent you from accessing certain aspects of our website where cookies are necessary.</p>

          <h3>Third Party Cookies</h3>
          <p>Some of our business partners use their own cookies or widgets on our website. We have no access to or control over them. Information collected by any of those cookies or widgets is governed by the privacy policy of the company that created it and not by us.</p>
        </section>

        <section>
          <h2>Purpose for Collection</h2>
          <p>We generally collect and process your personal information for various purposes, including:</p>
          <ul>
            <li><strong>goods or services purposes</strong> — such as collecting orders or requests for and providing our goods or services;</li>
            <li><strong>marketing purposes</strong> — such as pursuing lawful related marketing activities;</li>
            <li><strong>business purposes</strong> — such as internal audit, accounting, business planning, and joint ventures, disposals of business, or other proposed and actual transactions; and</li>
            <li><strong>legal purposes</strong> — such as handling claims, complying with regulations, or pursuing good governance.</li>
          </ul>
        </section>

        <section>
          <h2>Use</h2>
          <p>We may use your personal information to fulfil our obligations to you.</p>
          <p>We may send administrative messages and email updates to you about the website. In some cases, we may also send you primarily promotional messages. You can choose to opt-out of promotional messages.</p>
        </section>

        <section>
          <h2>Disclosure</h2>
          <h3>Sharing</h3>
          <p>We may share your personal information with:</p>
          <ul>
            <li>our employees and staff who require the information to perform their job responsibilities;</li>
            <li>our goods or services providers under contract who help provide certain goods or services or help with parts of our business operations, including fraud prevention, bill collection, marketing, and technology services;</li>
            <li>credit bureaus to report account information, as permitted by law;</li>
            <li>banking partners as required by credit card association rules; and</li>
            <li>other third parties who provide us with relevant services where appropriate.</li>
          </ul>

          <h3>Regulators & Law Enforcement</h3>
          <p>We may disclose your personal information as required by law or governmental audit, or if required by a subpoena or court order, to comply with any law, to protect the safety of any individual or the general public, or to prevent violation of our customer relationship terms.</p>

          <h3>No Selling</h3>
          <p>We will not sell personal information. No personal information will be disclosed to anyone except as provided in this privacy policy.</p>
        </section>

        <section>
          <h2>Security</h2>
          <p>We take the security of personal information very seriously and always do our best to comply with applicable data protection laws. Our hosting provider hosts our website in a secure server environment that uses a firewall and other advanced security measures to prevent interference or access from outside intruders. We authorise access to personal information only for those employees who require it to fulfil their job responsibilities.</p>
        </section>

        <section>
          <h2>Retention</h2>
          <p>We will only retain your personal information for as long as it is necessary to fulfil the purposes explicitly set out in this policy, unless retention of the record is required or authorised by law or you have consented to the retention of the record. During the period of retention, we will continue to abide by our non-disclosure obligations and will not share or sell your personal information.</p>
        </section>

        <section>
          <h2>Your Rights</h2>
          <p>You may choose to correct or update the personal information you have submitted to us by accessing your account online or by contacting us via email. You are able to review or update any personal information that we hold on you. Please note that in order to better protect you and safeguard your personal information, we take steps to verify your identity before granting you access to your account or making any corrections to your personal information.</p>
        </section>

        <section>
          <h2>Transfer to Another Country</h2>
          <p>We may transmit or transfer personal information outside of South Africa to a foreign country and process it in that country. Personal information may be stored on servers located outside South Africa in a foreign country whose laws protecting personal information may not be as stringent as South African law. By using our website and services, you consent to us processing your personal information in a foreign country whose laws regarding processing of personal information may be less stringent.</p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>We may amend this privacy policy from time to time. If we make material changes, we will notify you by email or by means of a notice on our website. We encourage you to periodically review this page for the latest information on our privacy practices.</p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>If you have any questions or concerns arising from this privacy policy or the way in which we handle personal information, please contact us at:</p>
          <p><strong>Email:</strong> <a href="mailto:info@arrandarr.com">info@arrandarr.com</a></p>
        </section>

      </div>

    </div>
  )
}

export default PrivacyPolicy
